import React from 'react'
import styled from 'styled-components'

const VERDICT_COLORS = {
  'Strong Fit': { bg: 'rgba(16,185,129,0.12)', fg: '#059669' },
  Fit: { bg: 'rgba(16,185,129,0.08)', fg: '#059669' },
  Borderline: { bg: 'rgba(245,158,11,0.12)', fg: '#b45309' },
  'Not Fit': { bg: 'rgba(239,68,68,0.12)', fg: '#dc2626' },
}

// What the candidate is told when their interview did not go far enough to be
// scored. Telling someone whose time simply ran out that they "ended" the
// interview is both wrong and alarming, so the copy follows `completionReason`
// (owned by student-node's helpers/aiInterviewCompletionReason.js). An
// unrecognised or missing reason keeps the original wording.
const INCOMPLETE_ENDINGS = {
  time_up: {
    label: 'Time Limit Reached',
    headline: "This interview's time limit was reached before enough of it was completed to generate a score.",
    detail: 'Nothing here counts against you. Please contact your recruiter if you would like another attempt.',
  },
  question_cap_reached: {
    label: 'Interview Ended Early',
    headline: 'This interview closed before enough of it was completed to generate a score.',
    detail: 'Nothing here counts against you. Please contact your recruiter if you would like another attempt.',
  },
  proctoring_violation: {
    label: 'Interview Not Completed',
    headline: 'This interview was closed automatically because the full-screen requirement was not met.',
    detail: 'A fit score is generated only when the interview is completed properly. Please contact your recruiter if you would like another attempt.',
  },
}

const DEFAULT_INCOMPLETE_ENDING = {
  label: 'Interview Not Completed',
  headline: 'This interview was ended before there was enough signal to score it.',
  detail: 'A fit score is generated only when the interview is completed properly. If this was a mistake, please contact your recruiter to retry.',
}

const describeIncompleteEnding = (completionReason) =>
  INCOMPLETE_ENDINGS[completionReason] || DEFAULT_INCOMPLETE_ENDING

const Card = styled.section`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 20px 24px;
  margin-bottom: 16px;
`
const Eyebrow = styled.div`
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6366f1;
  font-weight: 600;
  margin-bottom: 8px;
`
const FitWrap = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`
const ScoreNumber = styled.div`
  font-size: 38px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.05;
  span {
    font-size: 14px;
    color: #64748b;
    margin-left: 4px;
  }
`
const Verdict = styled.span`
  display: inline-block;
  padding: 6px 14px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 13px;
  background: ${(p) => p.colors.bg};
  color: ${(p) => p.colors.fg};
`
const ParamRow = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 90px 3fr;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f1f5f9;
  align-items: flex-start;
  &:last-child {
    border-bottom: none;
  }
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 6px;
  }
`
const Quote = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: #64748b;
  font-style: italic;
  border-left: 3px solid #e0e7ff;
  padding-left: 10px;
`

const stars = (n) => {
  const v = Math.max(0, Math.min(5, Number(n) || 0))
  return '★'.repeat(v) + '☆'.repeat(5 - v)
}

// Confirms a language change the candidate themselves asked for during a
// Hinglish interview. Phrased as a neutral record, not a warning — they made a
// reasonable request, it was honoured, and it carries no scoring penalty.
const LanguageSwitchNote = ({ languageSwitch }) => {
  if (!languageSwitch?.to) return null
  return (
    <Card style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
      <Eyebrow style={{ color: '#475569' }}>Interview language</Eyebrow>
      <div style={{ color: '#334155', fontSize: 14, lineHeight: 1.6 }}>
        You asked to continue in {languageSwitch.to}, so the rest of the interview was conducted in{' '}
        {languageSwitch.to}. This did not affect your score.
      </div>
    </Card>
  )
}

const AIInterviewReportCard = ({ report }) => {
  if (!report) return null
  const sc = report.score || report
  if (report.interviewIncomplete) {
    const ending = describeIncompleteEnding(report.completionReason)
    return (
      <>
        <LanguageSwitchNote languageSwitch={report.languageSwitch} />
        <Card style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
          <Eyebrow style={{ color: '#b45309' }}>{ending.label}</Eyebrow>
          <div style={{ fontSize: 15, color: '#0f172a', fontWeight: 600, marginTop: 6, marginBottom: 6 }}>
            {ending.headline}
          </div>
          <div style={{ color: '#475569', fontSize: 13, lineHeight: 1.6 }}>
            {ending.detail}
          </div>
        </Card>
      </>
    )
  }
  if (!sc || sc.overallScore == null) {
    return (
      <Card>
        <div style={{ color: '#64748b' }}>AI Interview report is being prepared. Check back in a few minutes.</div>
      </Card>
    )
  }
  const verdictColors = VERDICT_COLORS[sc.verdict] || { bg: '#f1f5f9', fg: '#475569' }
  return (
    <>
      <LanguageSwitchNote languageSwitch={report.languageSwitch} />
      <Card>
        <Eyebrow>Fit Score</Eyebrow>
        <FitWrap>
          <ScoreNumber>
            {Math.round(sc.overallScore || 0)}
            <span>/ 100</span>
          </ScoreNumber>
          {sc.verdict && <Verdict colors={verdictColors}>{sc.verdict}</Verdict>}
        </FitWrap>
        {sc.executiveSummary && (
          <div style={{ marginTop: 14, color: '#334155', fontSize: 14, lineHeight: 1.6 }}>
            {sc.executiveSummary}
          </div>
        )}
      </Card>

      {Array.isArray(sc.parameterScores) && sc.parameterScores.length > 0 && (
        <Card>
          <Eyebrow>Parameter breakdown</Eyebrow>
          {sc.parameterScores.map((p, i) => (
            <ParamRow key={i}>
              <div>
                <div style={{ fontWeight: 600, color: '#0f172a' }}>{p.name}</div>
                {p.rating_label && (
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{p.rating_label}</div>
                )}
              </div>
              <div style={{ color: '#f59e0b', letterSpacing: 2, fontSize: 14 }}>{stars(p.rating)}</div>
              <div style={{ color: '#334155', fontSize: 13, lineHeight: 1.55 }}>
                {p.analysis}
                {p.supporting_quote && <Quote>&ldquo;{p.supporting_quote}&rdquo;</Quote>}
              </div>
            </ParamRow>
          ))}
        </Card>
      )}

      {Array.isArray(sc.strengths) && sc.strengths.length > 0 && (
        <Card>
          <Eyebrow style={{ color: '#059669' }}>Strengths</Eyebrow>
          {sc.strengths.map((s, i) => (
            <div key={i} style={{ marginBottom: 10 }}>
              <div style={{ fontWeight: 600, color: '#0f172a' }}>
                ✓ {s.claim || s.summary || s}
              </div>
              {s.quote && <Quote style={{ borderLeftColor: '#6366f1' }}>&ldquo;{s.quote}&rdquo;</Quote>}
            </div>
          ))}
        </Card>
      )}

      {Array.isArray(sc.concerns) && sc.concerns.length > 0 && (
        <Card>
          <Eyebrow style={{ color: '#b45309' }}>Worth probing further</Eyebrow>
          {sc.concerns.map((c, i) => (
            <div key={i} style={{ marginBottom: 10 }}>
              <div style={{ fontWeight: 600, color: '#0f172a' }}>
                ⚠ {c.claim || c.summary || c}
              </div>
              {c.quote && <Quote style={{ borderLeftColor: '#f59e0b' }}>&ldquo;{c.quote}&rdquo;</Quote>}
              {c.impact && <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>Impact: {c.impact}</div>}
            </div>
          ))}
        </Card>
      )}

      {sc.scoreRationale && (
        <Card style={{ borderLeft: '4px solid #6366f1', background: '#f8f7ff' }}>
          <Eyebrow>Why this score</Eyebrow>
          <div style={{ color: '#334155', fontSize: 14, lineHeight: 1.6, fontWeight: 500 }}>
            {sc.scoreRationale}
          </div>
        </Card>
      )}

      {sc.recommendationText && (
        <Card>
          <Eyebrow>Recommendation</Eyebrow>
          <div style={{ color: '#334155', fontSize: 14, lineHeight: 1.65 }}>{sc.recommendationText}</div>
        </Card>
      )}
    </>
  )
}

export default AIInterviewReportCard
